import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { api, type JobSummary } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Icon, type IconName } from './icons';
import { Kbd, cx } from './ui';

type Item = { id: string; label: string; hint?: string; icon: IconName; to: string; group: 'Jobs' | 'Go to' };

const PAGES: Array<Omit<Item, 'group'>> = [
  { id: 'overview', label: 'Overview', icon: 'home', to: '/' },
  { id: 'jobs', label: 'Jobs', icon: 'briefcase', to: '/jobs' },
  { id: 'new-job', label: 'New job', hint: 'Pick a role and let the AI draft the interview', icon: 'plus', to: '/jobs/new' },
  { id: 'live', label: 'Live now', icon: 'live', to: '/live' },
  { id: 'analytics', label: 'Analytics', icon: 'chart', to: '/analytics' },
  { id: 'scheduler', label: 'Scheduler', icon: 'calendar', to: '/scheduler' },
  { id: 'settings', label: 'Settings', icon: 'settings', to: '/settings' },
];

/** Ctrl/⌘ K: jump to a job or a page by typing a few letters. */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const jobs = useQuery({ queryKey: ['jobs'], queryFn: () => api.get<JobSummary[]>('/api/jobs'), enabled: open, staleTime: 30_000 });

  useEffect(() => {
    if (open) {
      setQ('');
      setCursor(0);
      setTimeout(() => input.current?.focus(), 10);
    }
  }, [open]);

  const items = useMemo<Item[]>(() => {
    const s = q.trim().toLowerCase();
    const pages = [...PAGES, ...(user?.platformAdmin ? [{ id: 'console', label: 'Platform console', hint: 'Companies, plans, invoices, system', icon: 'shield' as const, to: '/admin' }] : [])]
      .filter((p) => !s || p.label.toLowerCase().includes(s))
      .map((p) => ({ ...p, group: 'Go to' as const }));
    const found = (jobs.data ?? [])
      .filter((j) => !s || `${j.title} ${j.location} ${j.department ?? ''}`.toLowerCase().includes(s))
      .slice(0, 8)
      .map<Item>((j) => ({ id: j.id, label: j.title, hint: `${j.location} · ${j.counts.total} leads · ${j.status.toLowerCase()}`, icon: 'briefcase', to: `/jobs/${j.id}`, group: 'Jobs' }));
    return s ? [...found, ...pages] : [...pages, ...found];
  }, [q, jobs.data, user?.platformAdmin]);

  useEffect(() => setCursor(0), [q]);

  if (!open) return null;
  const go = (it: Item) => {
    onClose();
    navigate(it.to);
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(items.length - 1, c + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
    } else if (e.key === 'Enter') {
      const it = items[cursor];
      if (it) go(it);
    } else if (e.key === 'Escape') onClose();
  };

  let lastGroup = '';
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-ink/60 p-4 pt-[12vh] backdrop-blur-sm" onMouseDown={onClose}>
      <div role="dialog" aria-modal="true" aria-label="Jump to" className="scale-in w-full max-w-xl overflow-hidden rounded-xl bg-surface-raised shadow-pop ring-1 ring-border" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b border-border px-4">
          <Icon name="search" className="h-5 w-5 text-muted-fg" />
          <input
            ref={input}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Jump to a job or a page…"
            className="h-14 flex-1 bg-transparent text-base text-fg placeholder:text-faint focus:outline-none"
            aria-label="Search"
          />
          <Kbd>esc</Kbd>
        </div>
        <ul className="scrollbar-thin max-h-[50vh] overflow-y-auto p-2" role="listbox">
          {items.length === 0 && <li className="px-3 py-8 text-center text-sm text-muted-fg">Nothing matches “{q}”.</li>}
          {items.map((it, i) => {
            const header = it.group !== lastGroup ? it.group : null;
            lastGroup = it.group;
            return (
              <li key={`${it.group}-${it.id}`}>
                {header && <p className="eyebrow px-3 pb-1 pt-2">{header}</p>}
                <button
                  type="button"
                  role="option"
                  aria-selected={i === cursor}
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => go(it)}
                  className={cx('flex w-full items-center gap-3 rounded-[9px] px-3 py-2.5 text-left transition', i === cursor ? 'bg-accent-soft text-fg' : 'text-fg-2 hover:bg-muted')}
                >
                  <span className={cx('inline-flex h-8 w-8 items-center justify-center rounded-[8px]', i === cursor ? 'bg-brand-gradient text-white' : 'bg-muted text-muted-fg')}>
                    <Icon name={it.icon} className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{it.label}</span>
                    {it.hint && <span className="block truncate text-xs text-muted-fg">{it.hint}</span>}
                  </span>
                  {i === cursor && <Kbd>↵</Kbd>}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
